import { useCallback, useRef } from "react"
import { useAuth } from "../../../context/AuthContext"
import { resolveAgendaEventColors } from "../utils/agendaColors"

const AGENDA_VIEW_STORAGE_KEY = "odontoflow_agenda_calendar_view"
const AGENDA_RANGE_START_STORAGE_KEY = "odontoflow_agenda_range_start"
const AGENDA_RANGE_END_STORAGE_KEY = "odontoflow_agenda_range_end"

export function buildAuthHeaders(includeJson = false, forcedClinicId = "") {
  const token = localStorage.getItem("odontoflow_token") || ""

  const headers = {}

  if (includeJson) {
    headers["Content-Type"] = "application/json"
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  if (forcedClinicId && forcedClinicId !== "ALL") {
    headers["x-clinic-id"] = forcedClinicId
  }

  return headers
}

async function authJsonFetch(url, options = {}, forcedClinicId = "") {
  const method = String(options?.method || "GET").toUpperCase()
  const includeJson =
    options?.headers?.["Content-Type"] === "application/json" ||
    options?.headers?.["content-type"] === "application/json" ||
    method !== "GET"

  const res = await fetch(url, {
    ...options,
    headers: {
      ...buildAuthHeaders(includeJson, forcedClinicId),
      ...(options.headers || {}),
    },
  })

  const data = await res.json().catch(() => null)
  return { res, data }
}

function resolveEventTreatmentId(item) {
  return (
    item?.treatmentId ||
    item?.treatment?.id ||
    item?.treatmentLog?.treatmentId ||
    item?.treatmentLog?.treatment?.id ||
    item?.treatmentLogs?.[0]?.treatmentId ||
    item?.treatmentLogs?.[0]?.treatment?.id ||
    item?.suggestedTreatmentId ||
    ""
  )
}

function resolveEventTreatmentName(item) {
  return (
    item?.treatmentName ||
    item?.treatment?.name ||
    item?.treatmentLog?.treatment?.name ||
    item?.treatmentLogs?.[0]?.treatment?.name ||
    item?.reason ||
    ""
  )
}

function getStoredAgendaView() {
  if (typeof window === "undefined") return "timeGridDay"
  return localStorage.getItem(AGENDA_VIEW_STORAGE_KEY) || "timeGridDay"
}

function normalizeDateKey(value) {
  if (!value) return ""
  return String(value).slice(0, 10)
}

function parseDateParts(dateKey) {
  const [year, month, day] = String(dateKey || "")
    .split("-")
    .map((item) => Number(item))

  if (!year || !month || !day) return null

  return { year, month, day }
}

function addDays(dateKey, days) {
  const parts = parseDateParts(dateKey)
  if (!parts) return ""

  const base = new Date(parts.year, parts.month - 1, parts.day)
  base.setDate(base.getDate() + days)

  const yyyy = base.getFullYear()
  const mm = String(base.getMonth() + 1).padStart(2, "0")
  const dd = String(base.getDate()).padStart(2, "0")

  return `${yyyy}-${mm}-${dd}`
}

function getWeekRangeFromDate(dateKey) {
  const parts = parseDateParts(dateKey)
  if (!parts) return []

  const base = new Date(parts.year, parts.month - 1, parts.day)
  const day = base.getDay()
  const diffToMonday = day === 0 ? -6 : 1 - day

  const monday = new Date(base)
  monday.setDate(base.getDate() + diffToMonday)

  const dates = []
  for (let i = 0; i < 7; i += 1) {
    const current = new Date(monday)
    current.setDate(monday.getDate() + i)

    const yyyy = current.getFullYear()
    const mm = String(current.getMonth() + 1).padStart(2, "0")
    const dd = String(current.getDate()).padStart(2, "0")

    dates.push(`${yyyy}-${mm}-${dd}`)
  }

  return dates
}

function getVisibleDateKeys(fallbackDate) {
  const view = getStoredAgendaView()

  if (view !== "timeGridWeek") {
    return [normalizeDateKey(fallbackDate)].filter(Boolean)
  }

  if (typeof window !== "undefined") {
    const start = normalizeDateKey(
      localStorage.getItem(AGENDA_RANGE_START_STORAGE_KEY) || "",
    )
    const endExclusive = normalizeDateKey(
      localStorage.getItem(AGENDA_RANGE_END_STORAGE_KEY) || "",
    )

    if (start && endExclusive) {
      const dates = []
      let cursor = start

      while (cursor && cursor < endExclusive) {
        dates.push(cursor)
        cursor = addDays(cursor, 1)
      }

      if (dates.length) return dates
    }
  }

  return getWeekRangeFromDate(fallbackDate)
}

function getLocalDateKey(value) {
  if (!value) return ""

  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ""

  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, "0")
  const dd = String(date.getDate()).padStart(2, "0")

  return `${yyyy}-${mm}-${dd}`
}

function buildBufferedDateKeys(dateKeys) {
  const set = new Set()

  ;(Array.isArray(dateKeys) ? dateKeys : [])
    .map((item) => normalizeDateKey(item))
    .filter(Boolean)
    .forEach((dateKey) => {
      set.add(addDays(dateKey, -1))
      set.add(dateKey)
      set.add(addDays(dateKey, 1))
    })

  return Array.from(set).filter(Boolean).sort()
}

function eventMatchesVisibleDateKeys(event, visibleDateSet) {
  if (!visibleDateSet?.size) return true

  const startKey = getLocalDateKey(event?.start || event?.startTime)
  const endKey = getLocalDateKey(event?.end || event?.endTime)

  return Boolean(
    (startKey && visibleDateSet.has(startKey)) ||
      (endKey && visibleDateSet.has(endKey)),
  )
}

function uniqBy(items, getKey) {
  const map = new Map()

  for (const item of items || []) {
    const key = getKey(item)
    if (!key) continue
    map.set(key, item)
  }

  return Array.from(map.values())
}


function normalizeAgendaPatientSearchText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
}

function getAgendaPatientDisplayName(patient) {
  return String(
    patient?.fullName ||
      patient?.name ||
      `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim() ||
      ""
  ).trim()
}

function normalizeAgendaPatientRows(data) {
  if (Array.isArray(data)) return data

  if (Array.isArray(data?.patients)) return data.patients
  if (Array.isArray(data?.items)) return data.items
  if (Array.isArray(data?.rows)) return data.rows
  if (Array.isArray(data?.data)) return data.data

  return []
}

function agendaPatientMatchesQuery(patient, query) {
  const normalizedQuery = normalizeAgendaPatientSearchText(query)

  if (!normalizedQuery) return true

  const haystack = normalizeAgendaPatientSearchText(
    [
      getAgendaPatientDisplayName(patient),
      patient?.firstName,
      patient?.lastName,
      patient?.phone,
      patient?.email,
      patient?.id,
    ]
      .filter(Boolean)
      .join(" ")
  )

  return haystack.includes(normalizedQuery)
}

function dedupeAgendaPatients(rows) {
  const map = new Map()

  for (const row of rows || []) {
    const key =
      row?.id ||
      [
        normalizeAgendaPatientSearchText(getAgendaPatientDisplayName(row)),
        normalizeAgendaPatientSearchText(row?.phone),
        normalizeAgendaPatientSearchText(row?.email),
      ].join("|")

    if (!key) continue
    if (!map.has(key)) map.set(key, row)
  }

  return Array.from(map.values())
}

export default function useAgendaData({
API_BASE,
  clinicId,
  date,
  patientQuery,
  newAppt,
  setPatients,
  setLoadingPatients,
  setNewAppt,
  setTreatments,
  setDoctors,
  setChairs,
  setHeldSlots,
  setEvents,
  setFinancialSummary,
  setLoadingFinancialSummary,
  setPaymentForm,
  setReceivableForm,
  setTreatmentPlans,
  setLoadingTreatmentPlans,
  setPatientSnapshot,
  setLoadingPatientSnapshot,
  setPatientOdontogram,
  setLoadingPatientOdontogram,
}) {
  const { user } = useAuth()
  const patientSearchRequestSeqRef = useRef(0)
  const patientRowsCacheRef = useRef({
    clinicId: "",
    query: "",
    rows: [],
  })

  const loadPatients = useCallback(
    async (cId = clinicId, query = patientQuery) => {
      const cleanClinicId = cId || clinicId
      const cleanQuery = String(query || "").trim()
      const normalizedQuery = normalizeAgendaPatientSearchText(cleanQuery)
      const requestId = patientSearchRequestSeqRef.current + 1

      patientSearchRequestSeqRef.current = requestId

      const isCurrentRequest = () => requestId === patientSearchRequestSeqRef.current

      const fetchPatientRows = async (url) => {
        const { res, data } = await authJsonFetch(url, {}, cleanClinicId)

        if (!res.ok) return []

        return normalizeAgendaPatientRows(data)
      }

      try {
        if (!cleanClinicId || cleanClinicId === "ALL") {
          patientRowsCacheRef.current = {
            clinicId: cleanClinicId || "",
            query: cleanQuery,
            rows: [],
          }

          setPatients([])
          return []
        }

        setLoadingPatients(true)

        const primaryParams = new URLSearchParams({
          clinicId: cleanClinicId,
          q: cleanQuery,
          limit: "20",
        })

        const primaryRows = await fetchPatientRows(
          `${API_BASE}/appointments/clinic-patients?${primaryParams.toString()}`,
        )

        if (!isCurrentRequest()) return primaryRows

        const primaryMatchedRows =
          normalizedQuery.length >= 2
            ? primaryRows.filter((patient) => agendaPatientMatchesQuery(patient, cleanQuery))
            : primaryRows

        let fallbackRows = []

        /**
         * Si el endpoint de agenda devuelve primeros 20 sin coincidir con lo escrito,
         * intentamos el endpoint general de pacientes para no perder nombres como “Sabina”.
         */
        if (normalizedQuery.length >= 2 && primaryMatchedRows.length === 0) {
          const fallbackParams = new URLSearchParams({
            clinicId: cleanClinicId,
            q: cleanQuery,
            limit: "20",
          })

          fallbackRows = await fetchPatientRows(
            `${API_BASE}/patients?${fallbackParams.toString()}`,
          )

          if (!isCurrentRequest()) return fallbackRows
        }

        const mergedRows = dedupeAgendaPatients([
          ...primaryMatchedRows,
          ...fallbackRows,
        ])

        const finalRows =
          normalizedQuery.length >= 2
            ? mergedRows.filter((patient) => agendaPatientMatchesQuery(patient, cleanQuery))
            : mergedRows

        const visibleRows = finalRows.slice(0, 20)

        if (!isCurrentRequest()) return visibleRows

        patientRowsCacheRef.current = {
          clinicId: cleanClinicId,
          query: cleanQuery,
          rows: visibleRows,
        }

        setPatients(visibleRows)

        return visibleRows
      } catch {
        if (isCurrentRequest()) {
          const cached = patientRowsCacheRef.current

          if (
            cached?.clinicId === cleanClinicId &&
            normalizeAgendaPatientSearchText(cached?.query) === normalizedQuery
          ) {
            setPatients(cached.rows || [])
            return cached.rows || []
          }

          setPatients([])
        }

        return []
      } finally {
        if (isCurrentRequest()) {
          setLoadingPatients(false)
        }
      }
    },
    [
      API_BASE,
      clinicId,
      patientQuery,
      setPatients,
      setLoadingPatients,
    ],
  )

  const loadTreatments = useCallback(
    async (cId = clinicId) => {
      try {
        if (!cId || cId === "ALL") {
          setTreatments([])
          return []
        }

        const { res, data } = await authJsonFetch(
          `${API_BASE}/treatments?clinicId=${encodeURIComponent(cId)}`,
          {},
          cId,
        )

        if (!res.ok) {
          setTreatments([])
          return []
        }

        const rows = Array.isArray(data) ? data : []

        setTreatments(rows)

        setNewAppt((prev) => {
          const selectedTreatment = rows.find((t) => t.id === prev.treatmentId)

          if (prev.treatmentId && selectedTreatment) {
            return {
              ...prev,
              duration: selectedTreatment?.durationMinutes || prev.duration || 30,
            }
          }

          return {
            ...prev,
            treatmentId: "",
            duration: prev.duration || 30,
          }
        })

        return rows
      } catch {
        setTreatments([])
        return []
      }
    },
    [API_BASE, clinicId, setTreatments, setNewAppt],
  )

  const loadDoctors = useCallback(
    async (cId = clinicId) => {
      try {
        if (!cId || cId === "ALL") {
          setDoctors([])
          return []
        }

        const { res, data } = await authJsonFetch(
          `${API_BASE}/multi-clinic/clinic-doctors?clinicId=${encodeURIComponent(cId)}`,
          {},
          cId,
        )

        if (!res.ok) {
          setDoctors([])
          return []
        }

        const rows = Array.isArray(data) ? data : []

        setDoctors(rows)
        return rows
      } catch {
        setDoctors([])
        return []
      }
    },
    [API_BASE, clinicId, setDoctors],
  )

  const loadChairs = useCallback(
    async (cId = clinicId) => {
      try {
        if (!cId || cId === "ALL") {
          setChairs([])
          return []
        }

        // Si en tu ChairsPage usas otra ruta, reemplaza solo esta URL.
        const { res, data } = await authJsonFetch(
          `${API_BASE}/chairs?clinicId=${encodeURIComponent(cId)}`,
          {},
          cId,
        )

        if (!res.ok) {
          setChairs([])
          return []
        }

        const rows = Array.isArray(data) ? data : []

        setChairs(rows)

        setNewAppt((prev) => {
          const exists = rows.some((chair) => chair.id === prev.chairId)

          return {
            ...prev,
            chairId: exists ? prev.chairId : "",
          }
        })

        return rows
      } catch {
        setChairs([])
        return []
      }
    },
    [API_BASE, clinicId, setChairs, setNewAppt],
  )

  const fetchHeldSlotsRaw = useCallback(
    async (cId, d) => {
      if (!cId || cId === "ALL") return []

      const params = new URLSearchParams({
        clinicId: cId,
        date: d,
      })

      const { res, data } = await authJsonFetch(
        `${API_BASE}/appointments/held-slots?${params.toString()}`,
        {},
        cId,
      )

      if (!res.ok) return []
      return Array.isArray(data) ? data : []
    },
    [API_BASE],
  )

  const loadHeldSlots = useCallback(
    async (cId = clinicId, d = date) => {
      try {
        const rows = await fetchHeldSlotsRaw(cId, d)
        setHeldSlots(rows)
        return rows
      } catch {
        setHeldSlots([])
        return []
      }
    },
    [clinicId, date, fetchHeldSlotsRaw, setHeldSlots],
  )

  const mapGlobalAgendaEvent = useCallback((a) => {
    const isHeld = Boolean(a.isHeld || a.status === "HELD")
    const isPlanLinked = Boolean(a.planItemId || a.treatmentPlanItemId)
    const treatmentId = resolveEventTreatmentId(a)
    const treatmentName = resolveEventTreatmentName(a)

    const colors = resolveAgendaEventColors({
      isHeld,
      isPlanLinked,
      source: a.source,
      clinicName: a.clinicName,
    })

    return {
      id: isHeld ? `held-${a.id}` : a.id,
      title: a.patientName || a.title || "Paciente",
      start: a.start,
      end: a.end,
      checkedIn: a.checkedIn,
      checkedInTime: a.checkedInTime || null,
      producedAmount: Number(a.producedAmount || 0),
      paidAmount: Number(a.paidAmount || 0),
      pendingAmount: Number(a.pendingAmount || 0),
      financialStatus: a.financialStatus || "NONE",
      ...colors,
      extendedProps: {
        clinicId: a.clinicId,
        clinicName: a.clinicName,
        doctorId: a.doctorId,
        doctorName: a.doctorName,
        chairId: a.chairId,
        chairName: a.chairName,
        patientId: a.patientId,
        patientName: a.patientName,
        treatmentId,
        treatmentName,
        reason: a.reason || "",
        status: a.status,
        source: a.source,
        checkedIn: a.checkedIn,
        checkedInTime: a.checkedInTime || null,
        producedAmount: Number(a.producedAmount || 0),
        paidAmount: Number(a.paidAmount || 0),
        pendingAmount: Number(a.pendingAmount || 0),
        financialStatus: a.financialStatus || "NONE",
        planItemId: a.planItemId || a.treatmentPlanItemId || null,
        planTitle: a.planTitle || null,
        isPlanLinked,
        isHeld,
        customTagColor:
          a.customTagColor ||
          a.markerColor ||
          a.colorTag ||
          a.tagColor ||
          a.customColor ||
          null,
      },
    }
  }, [])

  const mapAppointmentEvent = useCallback((a, cId) => {
    const isPlanLinked = Boolean(a.planItemId || a.treatmentPlanItemId)
    const treatmentId = resolveEventTreatmentId(a)
    const treatmentName = resolveEventTreatmentName(a)

    const colors = resolveAgendaEventColors({
      isHeld: false,
      isPlanLinked,
      source: a.source,
      clinicName: null,
    })

    return {
      id: a.id,
      title: a.title,
      start: a.start,
      end: a.end,
      source: a.source,
      checkedIn: a.checkedIn,
      checkedInTime: a.checkedInTime || null,
      producedAmount: Number(a.producedAmount || 0),
      paidAmount: Number(a.paidAmount || 0),
      pendingAmount: Number(a.pendingAmount || 0),
      financialStatus: a.financialStatus || "NONE",
      ...colors,
      extendedProps: {
        clinicId: cId,
        patientId: a.patientId,
        patientName: a.patientName,
        doctorId: a.doctorId,
        doctorName: a.doctorName,
        chairId: a.chairId,
        chairName: a.chairName,
        treatmentId,
        treatmentName,
        reason: a.reason || "",
        status: a.status,
        source: a.source,
        checkedIn: a.checkedIn,
        checkedInTime: a.checkedInTime || null,
        producedAmount: Number(a.producedAmount || 0),
        paidAmount: Number(a.paidAmount || 0),
        pendingAmount: Number(a.pendingAmount || 0),
        financialStatus: a.financialStatus || "NONE",
        planItemId: a.planItemId || a.treatmentPlanItemId || null,
        planTitle: a.planTitle || null,
        isPlanLinked,
        customTagColor:
          a.customTagColor ||
          a.markerColor ||
          a.colorTag ||
          a.tagColor ||
          a.customColor ||
          null,
      },
    }
  }, [])

  const mapHeldEvent = useCallback((h, cId) => {
    const isPlanLinked = Boolean(h.planItemId || h.treatmentPlanItemId)
    const treatmentId = resolveEventTreatmentId(h)
    const treatmentName = resolveEventTreatmentName(h) || "Reserva temporal"

    const colors = resolveAgendaEventColors({
      isHeld: true,
      isPlanLinked,
      source: h.source,
      clinicName: null,
    })

    return {
      id: `held-${h.id}`,
      title: h.patient
        ? `${h.patient.firstName} ${h.patient.lastName}`
        : "RESERVA TEMPORAL",
      start: h.startTime,
      end: h.endTime,
      ...colors,
      extendedProps: {
        clinicId: cId,
        status: h.status,
        reason: h.reason || "Reserva temporal",
        isHeld: true,
        patientId: h.patientId,
        patientName: h.patient
          ? `${h.patient.firstName} ${h.patient.lastName}`
          : null,
        doctorId: h.doctorId,
        doctorName: h.doctor?.name || null,
        treatmentId,
        treatmentName,
        planItemId: h.planItemId || h.treatmentPlanItemId || null,
        planTitle: h.planTitle || null,
        isPlanLinked,
        customTagColor:
          h.customTagColor ||
          h.markerColor ||
          h.colorTag ||
          h.tagColor ||
          h.customColor ||
          null,
      },
    }
  }, [])

  const loadAgenda = useCallback(
    async (cId = clinicId, d = date, heldRows = null) => {
      try {
        if (!cId) {
          setEvents([])
          setHeldSlots([])
          return []
        }

        const visibleDates = getVisibleDateKeys(d)
        const queryDates = buildBufferedDateKeys(visibleDates)
        const visibleDateSet = new Set(visibleDates)

        if (cId === "ALL") {
          if (!user?.id) {
            setEvents([])
            return []
          }

          const dailyResults = await Promise.all(
            queryDates.map(async (dateKey) => {
              const params = new URLSearchParams({
                date: dateKey,
              })

              const { res, data } = await authJsonFetch(
                `${API_BASE}/multi-clinic/global-agenda?${params.toString()}`,
                {},
                "",
              )

              if (!res.ok) return []
              return Array.isArray(data) ? data : []
            }),
          )

          const flatRows = dailyResults.flat()
          const mapped = uniqBy(
            flatRows.map(mapGlobalAgendaEvent),
            (item) => `${item.id}-${item.start}-${item.end}`,
          ).filter((item) => eventMatchesVisibleDateKeys(item, visibleDateSet))

          setEvents(mapped)
          return mapped
        }

        const agendaResults = await Promise.all(
          queryDates.map(async (dateKey) => {
            const agendaParams = new URLSearchParams({
              clinicId: cId,
              date: dateKey,
            })

            const { res, data } = await authJsonFetch(
              `${API_BASE}/appointments/agenda?${agendaParams.toString()}`,
              {},
              cId,
            )

            if (!res.ok) return []
            return Array.isArray(data) ? data : []
          }),
        )

        const agendaRows = agendaResults.flat()

        console.table(
          agendaRows.map((a) => ({
            id: a.id,
            title: a.title,
            chairId: a.chairId,
            chairName: a.chairName,
            doctorId: a.doctorId,
            doctorName: a.doctorName,
          }))
        )

        let heldAggregate = []

        if (Array.isArray(heldRows) && queryDates.length === 1) {
          heldAggregate = heldRows
        } else {
          const heldResults = await Promise.all(
            queryDates.map((dateKey) => fetchHeldSlotsRaw(cId, dateKey)),
          )
          heldAggregate = heldResults.flat()
        }

        setHeldSlots(heldAggregate)

        const appointmentEvents = agendaRows
          .map((a) => mapAppointmentEvent(a, cId))
          .filter((item) => eventMatchesVisibleDateKeys(item, visibleDateSet))

        console.table(
          appointmentEvents.map((e) => ({
            id: e.id,
            title: e.title,
            chairId: e.extendedProps?.chairId,
            chairName: e.extendedProps?.chairName,
            doctorId: e.extendedProps?.doctorId,
            doctorName: e.extendedProps?.doctorName,
          }))
        )
        const heldEvents = heldAggregate
          .map((h) => mapHeldEvent(h, cId))
          .filter((item) => eventMatchesVisibleDateKeys(item, visibleDateSet))

        const allEvents = uniqBy(
          [...appointmentEvents, ...heldEvents],
          (item) => `${item.id}-${item.start}-${item.end}`,
        )

        setEvents(allEvents)
        return allEvents
      } catch {
        setEvents([])
        setHeldSlots([])
        return []
      }
    },
    [
      API_BASE,
      user,
      clinicId,
      date,
      setEvents,
      setHeldSlots,
      fetchHeldSlotsRaw,
      mapGlobalAgendaEvent,
      mapAppointmentEvent,
      mapHeldEvent,
    ],
  )

  const loadFinancialSummary = useCallback(
    async (appointmentId, clinicIdOverride = clinicId) => {
      try {
        setLoadingFinancialSummary(true)

        const params = new URLSearchParams({
          appointmentId,
        })

        const forcedClinicId =
          clinicIdOverride && clinicIdOverride !== "ALL" ? clinicIdOverride : ""

        const { res, data } = await authJsonFetch(
          `${API_BASE}/appointments/financial-summary?${params.toString()}`,
          {},
          forcedClinicId,
        )

        if (!res.ok) {
          setFinancialSummary(null)
          return null
        }

        setFinancialSummary(data || null)

        const pendingReceivables = Number(data?.pendingReceivables || 0)

        if (pendingReceivables > 0) {
          setPaymentForm((prev) => ({
            ...prev,
            amount: String(pendingReceivables),
          }))
          setReceivableForm((prev) => ({
            ...prev,
            totalAmount: String(pendingReceivables),
          }))
        } else {
          setPaymentForm((prev) => ({
            ...prev,
            amount: "",
          }))
          setReceivableForm((prev) => ({
            ...prev,
            totalAmount: "",
          }))
        }

        return data || null
      } catch {
        setFinancialSummary(null)
        return null
      } finally {
        setLoadingFinancialSummary(false)
      }
    },
    [
      API_BASE,
      clinicId,
      setFinancialSummary,
      setLoadingFinancialSummary,
      setPaymentForm,
      setReceivableForm,
    ],
  )

  const loadPatientTreatmentPlans = useCallback(
    async (patientId, clinicIdOverride = clinicId) => {
      try {
        if (!patientId || !clinicIdOverride || clinicIdOverride === "ALL") {
          setTreatmentPlans([])
          return
        }

        setLoadingTreatmentPlans(true)

        const params = new URLSearchParams({
          patientId,
          clinicId: clinicIdOverride,
        })

        const { res, data } = await authJsonFetch(
          `${API_BASE}/appointments/patient-treatment-plans?${params.toString()}`,
          {},
          clinicIdOverride,
        )

        if (!res.ok) {
          setTreatmentPlans([])
          return
        }

        setTreatmentPlans(Array.isArray(data) ? data : [])
      } catch {
        setTreatmentPlans([])
      } finally {
        setLoadingTreatmentPlans(false)
      }
    },
    [API_BASE, clinicId, setTreatmentPlans, setLoadingTreatmentPlans],
  )

  const loadPatientSnapshot = useCallback(
    async (patientId, clinicIdOverride = clinicId) => {
      try {
        if (!patientId || !clinicIdOverride || clinicIdOverride === "ALL") {
          setPatientSnapshot(null)
          return
        }

        setLoadingPatientSnapshot(true)

        const params = new URLSearchParams({
          patientId,
          clinicId: clinicIdOverride,
        })

        const { res, data } = await authJsonFetch(
          `${API_BASE}/appointments/patient-clinical-snapshot?${params.toString()}`,
          {},
          clinicIdOverride,
        )

        if (!res.ok) {
          setPatientSnapshot(null)
          return
        }

        setPatientSnapshot(data || null)
      } catch {
        setPatientSnapshot(null)
      } finally {
        setLoadingPatientSnapshot(false)
      }
    },
    [API_BASE, clinicId, setPatientSnapshot, setLoadingPatientSnapshot],
  )

  const loadPatientOdontogram = useCallback(
    async (patientId, clinicIdOverride = clinicId) => {
      try {
        if (!patientId || !clinicIdOverride || clinicIdOverride === "ALL") {
          setPatientOdontogram(null)
          return
        }

        setLoadingPatientOdontogram(true)

        const params = new URLSearchParams({
          patientId,
          clinicId: clinicIdOverride,
        })

        const { res, data } = await authJsonFetch(
          `${API_BASE}/appointments/patient-odontogram-workspace?${params.toString()}`,
          {},
          clinicIdOverride,
        )

        if (!res.ok) {
          setPatientOdontogram(null)
          return
        }

        const history = Array.isArray(data?.history) ? data.history : []
        const latestEntries = Array.isArray(data?.latestEntries) ? data.latestEntries : []
        const allEntries = Array.isArray(data?.allEntries)
          ? data.allEntries
          : history.length
            ? history
            : latestEntries

        setPatientOdontogram({
          ...data,
          history,
          latestEntries,
          allEntries,
          entries: allEntries,
        })
      } catch {
        setPatientOdontogram(null)
      } finally {
        setLoadingPatientOdontogram(false)
      }
    },
    [API_BASE, clinicId, setPatientOdontogram, setLoadingPatientOdontogram],
  )

  return {
    loadPatients,
    loadTreatments,
    loadDoctors,
    loadChairs,
    loadHeldSlots,
    loadAgenda,
    loadFinancialSummary,
    loadPatientTreatmentPlans,
    loadPatientSnapshot,
    loadPatientOdontogram,
  }
}